/**
 * CatastroInlinePanel.tsx — Panel catastral inline en fila expandible P2
 *
 * Muestra superficie, plantas, uso, aforo estimado y accesibilidad
 * directamente en la fila expandida, sin necesidad de abrir el mapa.
 *
 * @task UI-P2-CAT-01
 * @version 1.0.0
 */

import type { FC, CSSProperties } from 'react';
import type { CatastroR2Parcel } from '@/types/catastroR2';
import { tipologiaACodigoCatastro } from '@/types/catastroR2';
import type { TipologiaPTEL } from '@/types/tipologia';
import { TIPOLOGIAS } from '@/constants/export.constants';
import { enrichCatastroTooltip } from '@/services/catastro/catastroEnricher';
import { colors, typography, borderRadius } from '@/design-system/v2';

// ============================================================================
// PROPS
// ============================================================================

export interface CatastroInlinePanelProps {
  parcel?: CatastroR2Parcel;
  className?: string;
  /**
   * `CATU` (20-ago-2026) · DOP-CATU-AVISO=(b1). Coordenada UTM30 del elemento al
   * que se atribuye la parcela. Si se pasa, el panel encabeza con la distancia
   * parcela↔elemento; si no, no la pinta (graceful, mismo criterio DOP-CRUI-2=a).
   *
   * POR QUÉ EXISTE: los atributos de abajo —superficie, plantas, uso, aforo— se
   * presentan como del elemento, y en un tercio de los casos no lo son.
   * `AUDIT_CFU1` §4.2 midió **34,2 % DISCORDANTE** por uso sobre las 111 parcelas
   * atadas, y la discordancia llega al 92,7 % en el subconjunto donde el filtro
   * de uso aplica. Sin señal alguna, eso es severidad **B** del INDEX: sale algo
   * presentado como cierto sin serlo, y sin avisar.
   *
   * La distancia es el discriminador que separa: `AUDIT_CFU1` §4.1 midió los
   * aciertos a p50 **205 m** y los errores que preocupan a 3 314 m (Iglesia de la
   * Asunción de Alfacar) y 4 008 m (Embalse de Colomera).
   */
  elementoCoord?: { x: number; y: number };
  /**
   * `CATUSO` (11-sep-2026) · DOP-CATUSO-4=(a′). Tipología PTEL del elemento,
   * calculada por `Step2Page` como `infraTipologiaAPTEL(geocoding.classification.type)`
   * — la misma composición que midió `AUDIT_CFU1`. Si se pasa y su lista de usos
   * (`tipologiaACodigoCatastro`) no es vacía, el panel avisa en ámbar cuando el
   * `uso_cod` de la parcela no está en ella. Sin *prop*, o con `GENERICO` (lista
   * vacía), no hay opinión por uso (DOP-CATUSO-1=a): se avisa donde hay
   * conocimiento medido y se calla donde no.
   *
   * POR QUÉ EXISTE: la distancia sola no separa la parcela vecina equivocada.
   * `CATAMB` vio la Iglesia de la Asunción a 99 m (gris) de una parcela
   * Industrial. Medido sobre el corpus `CFU1`: 38/41 parcelas con lista son
   * discordantes por uso (Alfacar 10/10).
   */
  tipologia?: TipologiaPTEL;
}

// ============================================================================
// CONSTANTES DE COLOR (aprobadas en C3.14 / UI-P2-CAT-01)
// ============================================================================

const COLOR_ACCESIB_ALTA  = colors.success; // Alta — lima
const COLOR_ACCESIB_MEDIA = colors.warning; // Media — ámbar
const COLOR_ACCESIB_BAJA  = colors.error; // Baja/Desconocida — rojo suave
const COLOR_REFORMA       = colors.accent.ochre; // Reforma — ocre (celda Constr.–Reforma) · VIS-2b-ii

// ============================================================================
// STYLES
// ============================================================================

const panelStyle: CSSProperties = {
  display: 'grid',
  // VIS-3b (DOP-VIS3B-COLUMNAS=a): 3→2 columnas del mockup P2_REDISENO v1.
  // Decidido con mini-mockup sobre datos reales: a 2 col la parcela rica gana
  // una fila, pero las etiquetas largas («Aforo estimado (CTE DB-SI)») envuelven
  // en 2 líneas en vez de 3; en parcela pobre y rústica solo-cultivos —el caso
  // mayoritario en producción— las dos rejillas son idénticas.
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '8px 16px',
  background: colors.bg.tertiary,
  padding: '10px 20px',
  borderBottom: `1px solid ${colors.border.DEFAULT}`,
  borderTop: `1px solid ${colors.border.light}`,
};

const cellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const labelStyle: CSSProperties = {
  fontSize: typography.fontSize['2xs'],
  color: colors.fg.subtle,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: typography.fontWeight.medium,
};

const valueStyle: CSSProperties = {
  fontSize: typography.fontSize.sm,
  color: colors.fg.secondary,
  fontWeight: typography.fontWeight.medium,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};


const semaphoreStyle = (color: string): CSSProperties => ({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: color,
  flexShrink: 0,
});

// Bloque «Cultivos» (cat-rich-1, solo rústicas): ancho completo bajo el grid 3-col
const cultivosBlockStyle: CSSProperties = {
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '2px',
  paddingTop: '8px',
  borderTop: `1px solid ${colors.border.light}`,
};

const cultivosListStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 10px',
};

const cultivoItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '4px',
  fontSize: typography.fontSize.sm,
  color: colors.fg.secondary,
  fontWeight: typography.fontWeight.medium,
};

const cultivoSupStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: colors.fg.subtle,
};

const cultivoMoreStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
  fontStyle: 'italic',
};

// Matiz subordinado dentro de una celda (exactitud del año · tipo de reforma).
const qualifierStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: colors.fg.subtle,
};

// Constr.-Reforma (CCD1 V1): linea degradada construccion->reforma + extremos.
const reformaFlowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
};
const reformaLineStyle: CSSProperties = {
  flex: 1,
  height: '2px',
  minWidth: '22px',
  borderRadius: '1px',
  background: `linear-gradient(90deg, ${colors.fg.secondary}, ${COLOR_REFORMA})`,
};
const reformaDotConstrStyle: CSSProperties = {
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: colors.fg.secondary,
  flexShrink: 0,
};
const reformaDotStyle: CSSProperties = {
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: COLOR_REFORMA,
  flexShrink: 0,
};
const reformaYearStyle: CSSProperties = {
  color: COLOR_REFORMA,
};
const reformaTipoStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: COLOR_REFORMA,
  alignSelf: 'flex-end',
};
const reformaLabelsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};

// ============================================================================
// HELPERS
// ============================================================================

function accesibilidadColor(nivel: 'probable' | 'verificar' | 'desconocida'): string {
  if (nivel === 'probable')  return COLOR_ACCESIB_ALTA;
  if (nivel === 'verificar') return COLOR_ACCESIB_MEDIA;
  return COLOR_ACCESIB_BAJA;
}

/** Nº máx. de subparcelas de cultivo mostradas antes de resumir con «+N más». */
const MAX_CULTIVOS = 4;

interface CultivoView {
  denom: string;
  sup: number;
}

/**
 * Lectura defensiva de una subparcela de cultivo (`cat-rich-1`, tipo laxo
 * `Record<string, unknown>`). Prioriza `denominacion` (ya legible en el dato,
 * p. ej. «Olivos secano»); cae a `tipo` y, en último término, a «—».
 * No decodifica códigos sin tabla verificada (`calificacion`, `intensidad`).
 */
function readCultivo(c: Record<string, unknown>): CultivoView {
  const denom =
    typeof c['denominacion'] === 'string' && c['denominacion'].trim() !== ''
      ? c['denominacion']
      : typeof c['tipo'] === 'string' && c['tipo'].trim() !== ''
        ? c['tipo']
        : '—';
  const sup = typeof c['sup_m2'] === 'number' ? c['sup_m2'] : 0;
  return { denom, sup };
}

/**
 * Indicador de exactitud del año de construcción (registro `.CAT` tipo 13).
 * Tabla oficial DGC «Preguntas frecuentes formato CAT» §12. `corto` se pinta
 * junto al año; `largo` va en `title`. Código no listado → null (no se inventa).
 */
function exactitudAnyo(code: string | undefined): { corto: string; largo: string } | null {
  switch (code) {
    case 'E': return { corto: 'exacto',            largo: 'Año exacto' };
    case '+': return { corto: 'aprox. por encima', largo: 'Año aproximado por encima' };
    case '-': return { corto: 'aprox. por debajo', largo: 'Año aproximado por debajo' };
    case 'C': return { corto: 'aprox.',            largo: 'Año aproximado' };
    default:  return null;
  }
}

/**
 * Tipo de reforma o rehabilitación (registro `.CAT` tipo 14). Tabla oficial DGC
 * «Preguntas frecuentes formato CAT» §14. Código no listado → null (no se
 * renderiza etiqueta inventada; gate `CR2H18089`).
 */
function tipoReformaLabel(code: string | undefined): string | null {
  switch (code) {
    case 'R': return 'Rehabilitación integral';
    case 'O': return 'Reforma total';
    case 'E': return 'Reforma media';
    case 'I': return 'Reforma mínima';
    default:  return null;
  }
}

/**
 * Etiqueta corta del tipo de reforma (palabra clave bajo el año de reforma).
 * Mismas 4 clases DGC §14. Código no listado -> null (no inventa).
 */
function tipoReformaCorto(code: string | undefined): string | null {
  switch (code) {
    case 'R': return 'Integral';
    case 'O': return 'Total';
    case 'E': return 'Media';
    case 'I': return 'Mínima';
    default:  return null;
  }
}

/**
 * `CATU` · Procedencia de la parcela: distancia euclídea al elemento en UTM30.
 *
 * Ambas coordenadas son EPSG:25830 en metros (la parcela llega reproyectada;
 * `srs` del dato lo declara), así que la euclídea es la distancia real y no hace
 * falta geodésica.
 *
 * UN SOLO CORTE, y es el medido: **100 m**. Es el que `AUDIT_CFU1` §4.2 usa para
 * partir «aciertos plausibles» (14 casos ≤100 m, entre ellos cuatro depósitos de
 * agua a 0 m atados por polígono/parcela) de «errores plausibles» (24 casos
 * >100 m). No se añade un segundo umbral: no está medido y no se improvisa
 * (`OBS-GEO-CATASTRO-PREFIJO-VIA-DEMASIADO-CORTO` sentó el precedente).
 */
function distanciaAlElemento(
  parcel: CatastroR2Parcel,
  coord: { x: number; y: number } | undefined,
): { metros: number; lejos: boolean } | null {
  if (!coord) return null;
  if (typeof parcel.x !== 'number' || typeof parcel.y !== 'number') return null;
  if (!Number.isFinite(parcel.x) || !Number.isFinite(parcel.y)) return null;
  if (!Number.isFinite(coord.x) || !Number.isFinite(coord.y)) return null;
  const metros = Math.hypot(parcel.x - coord.x, parcel.y - coord.y);
  if (!Number.isFinite(metros)) return null;
  return { metros, lejos: metros > 100 };
}

/** Distancia en RAE: coma decimal bajo 1 km, sin decimales por encima. */
function formatearDistancia(metros: number): string {
  if (metros < 1000) return `${Math.round(metros).toLocaleString('es-ES')} m`;
  return `${(metros / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })} km`;
}

/**
 * `CATUSO` · Discordancia por uso: el `uso_cod` DGC de la parcela no está en la
 * lista de usos compatibles de la tipología (DOP-CATUSO-1=a).
 *
 * Devuelve `null` («sin opinión») si no hay tipología, si su lista es vacía
 * (`GENERICO`) o si la parcela no trae `uso_cod`. La tabla es la de
 * `tipologiaACodigoCatastro` (medida en Colomera); no se ajusta aquí.
 */
function usoIncompatible(
  parcel: CatastroR2Parcel,
  tipologia: TipologiaPTEL | undefined,
): { usoCod: string; tipologia: TipologiaPTEL } | null {
  if (!tipologia) return null;
  const usos = tipologiaACodigoCatastro(tipologia);
  if (usos.length === 0) return null;
  const usoCod = typeof parcel.uso_cod === 'string' ? parcel.uso_cod.trim() : '';
  if (usoCod === '') return null;
  return usos.includes(usoCod) ? null : { usoCod, tipologia };
}

/** Etiqueta legible del uso DGC para la línea de procedencia; el código si no hay etiqueta. */
function usoLabel(parcel: CatastroR2Parcel, usoCod: string): string {
  const uso = typeof parcel.uso === 'string' ? parcel.uso.trim() : '';
  return uso !== '' && uso !== 'Desconocido' ? uso : usoCod;
}

/** `CATUSO` · etiqueta bajo aforo/accesibilidad cuando la parcela es dudosa (DOP-3=a). */
const noVerificadaStyle: CSSProperties = {
  fontSize: typography.fontSize['2xs'],
  color: colors.fg.subtle,
  fontStyle: 'italic',
};

const procedenciaStyle: CSSProperties = {
  gridColumn: '1 / -1',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: typography.fontSize['2xs'],
  color: colors.fg.subtle,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const CatastroInlinePanel: FC<CatastroInlinePanelProps> = ({
  parcel,
  className,
  elementoCoord,
  tipologia,
}) => {
  if (!parcel) return null;

  const distancia = distanciaAlElemento(parcel, elementoCoord);
  const discordancia = usoIncompatible(parcel, tipologia);

  /**
   * `CATUSO` · La parcela es «dudosa» si está lejos (>100 m) **o** su uso no
   * es el esperado para la tipología. Cualquiera de las dos causas pinta la
   * línea en ámbar y atenúa lo que se deriva de la parcela (aforo, accesibilidad).
   */
  const lejos = distancia?.lejos === true;
  const dudosa = lejos || discordancia !== null;

  /**
   * La línea va DENTRO del panel y ANTES de los atributos, también en el estado
   * vacío: una parcela sin datos a 3 km es justo el caso en que más importa que
   * se vea. Sin `elementoCoord` ni discordancia no se pinta nada — no se
   * inventa procedencia. DOP-CATUSO-2=(a): una sola línea con los motivos
   * acumulados, para que el usuario vea **por qué**.
   */
  const motivos: string[] = [];
  if (lejos) motivos.push('verificar que le corresponde');
  if (discordancia !== null) {
    motivos.push(
      `uso ${usoLabel(parcel, discordancia.usoCod)}, no esperado para ${TIPOLOGIAS[discordancia.tipologia].label}`,
    );
  }
  const procedencia = distancia === null && discordancia === null ? null : (
    <span style={procedenciaStyle} data-testid="catastro-procedencia">
      <span
        style={semaphoreStyle(dudosa ? COLOR_ACCESIB_MEDIA : colors.fg.subtle)}
        aria-hidden="true"
      />
      <span>
        {distancia !== null
          ? `Parcela a ${formatearDistancia(distancia.metros)} del elemento`
          : 'Parcela atribuida al elemento'}
        {motivos.length > 0 ? ` — ${motivos.join(' · ')}` : ''}
      </span>
    </span>
  );

  const data = enrichCatastroTooltip(parcel);

  const showSuperficie = data.superficie > 0;
  const showPlantas    = data.plantas    > 0;
  // cat-rich-1: año de construcción (RPB1 #2) + cultivos rústicas. Render graceful
  // (DOP-CRUI-2=a): la fila se omite si el campo no viene en el dato servido.
  const showAntiguedad = typeof parcel.antiguedad === 'number' && parcel.antiguedad > 0;
  const cultivos       = Array.isArray(parcel.cultivos) ? parcel.cultivos : [];

  // CCD1: 3 campos diferidos cat-rich-1 (tablas oficiales DGC verificadas).
  // Exactitud cualifica al año de constr. → no se muestra si no hay año.
  // Reforma = mismo evento → año (RAE: sin millares) + tipo decodificado; celda
  // ausente si no hay ni año ni tipo decodable (graceful, DOP-2=a).
  const exactitud   = showAntiguedad ? exactitudAnyo(parcel.antiguedad_exactitud) : null;
  const anyoReforma = typeof parcel.anyo_reforma === 'number' && parcel.anyo_reforma > 0
    ? parcel.anyo_reforma
    : null;
  const tipoRef     = tipoReformaLabel(parcel.tipo_reforma);
  const tipoCorto   = tipoReformaCorto(parcel.tipo_reforma);
  const showReforma = anyoReforma !== null || tipoRef !== null;

  // Punto 4: las celdas «de edificio» (Aforo, Accesibilidad) solo tienen sentido
  // con construcción. Rústica solo-cultivos (sin sup. construida, plantas ni año)
  // no tiene aforo ni accesibilidad → se ocultan (evita «aforo» de un olivar).
  const supConstruida  = typeof parcel.sup_construida === 'number' ? parcel.sup_construida : 0;
  const hayEdificacion = supConstruida > 0 || showPlantas || showAntiguedad;

  // VIS-3 (RED-6-CAT-UI): estado vacío explícito. Con parcela presente pero sin
  // ningún dato renderizable (caso mayoritario en producción), el silencio de las
  // celdas omitidas es ambiguo («¿no hay datos o falló la carga?»). Mensaje del
  // mockup P2_REDISENO v1 en su lugar. «Uso» = 'Desconocido' no cuenta como dato.
  const sinDatos =
    !showSuperficie && !showPlantas && !showReforma && !hayEdificacion &&
    cultivos.length === 0 && (!data.uso || data.uso === 'Desconocido');

  if (sinDatos) {
    return (
      <div style={panelStyle} className={className} data-testid="catastro-inline-panel">
        {procedencia}
        <span
          style={{ gridColumn: '1 / -1', fontSize: typography.fontSize.xs, color: colors.fg.muted }}
          data-testid="catastro-sin-datos"
        >
          Sin datos catastrales para este elemento.
        </span>
      </div>
    );
  }

  return (
    <div style={panelStyle} className={className} data-testid="catastro-inline-panel">

      {procedencia}

      {/* Superficie */}
      {/* `ALBCAT-a` · DOP-5=(a): es de la parcela, no del edificio del elemento. */}
      {showSuperficie && (
        <div style={cellStyle} data-testid="catastro-superficie"
          title="Superficie construida de la parcela (Catastro). No es el área cerrada del plan ni da plazas.">
          <span style={labelStyle}>Superficie construida de la parcela</span>
          <span style={valueStyle}>{data.superficie.toLocaleString('es-ES')} m²</span>
        </div>
      )}

      {/* Plantas */}
      {showPlantas && (
        <div style={cellStyle}>
          <span style={labelStyle}>Plantas</span>
          <span style={valueStyle}>{data.plantas} nP</span>
        </div>
      )}

      {/* Construccion / Reforma (cat-rich-1 · CCD1, rediseño V1 punto 5).
          Combinado con linea degradada (constr.->reforma) cuando hay anyo de
          constr. + anyo de reforma; si solo uno, celda simple. Exactitud y tipo
          de reforma -> tooltip (RAE: anyos sin separador de millares). */}
      {showAntiguedad && anyoReforma !== null && (
        <div style={cellStyle} data-testid="catastro-constr-reforma">
          <span style={reformaLabelsStyle}>
            <span style={labelStyle}>Constr.</span>
            <span style={labelStyle}>Reforma</span>
          </span>
          <span
            style={{ ...valueStyle, ...reformaFlowStyle }}
            title={`Construcción ${parcel.antiguedad}${exactitud ? ` (${exactitud.corto})` : ''} · Reforma ${anyoReforma}${tipoRef ? ` (${tipoRef})` : ''}`}
          >
            <span>{parcel.antiguedad}</span>
            <span style={reformaDotConstrStyle} aria-hidden="true" />
            <span style={reformaLineStyle} aria-hidden="true" />
            <span style={reformaDotStyle} aria-hidden="true" />
            <span style={reformaYearStyle}>{anyoReforma}</span>
          </span>
          {tipoCorto && <span style={reformaTipoStyle}>{tipoCorto}</span>}
        </div>
      )}

      {showAntiguedad && anyoReforma === null && (
        <div style={cellStyle}>
          <span style={labelStyle}>Año de constr.</span>
          <span style={valueStyle} title={exactitud ? `Año ${exactitud.corto}` : undefined}>
            {parcel.antiguedad}
          </span>
        </div>
      )}

      {!showAntiguedad && showReforma && (
        <div style={cellStyle} data-testid="catastro-reforma">
          <span style={labelStyle}>Reforma</span>
          <span style={reformaYearStyle} title={tipoRef ?? undefined}>
            {anyoReforma !== null ? anyoReforma : tipoRef}
          </span>
        </div>
      )}

      {/* Uso */}
      <div style={cellStyle}>
        <span style={labelStyle}>Uso</span>
        <span style={valueStyle}>{data.uso}</span>
      </div>

      {/* `AFORET` · DOP-1=(a): la casilla «Aforo estimado (CTE DB-SI)» se retira
          hasta `AFORCAT`. La cifra salía inflada (un depósito de agua de Colomera
          con 196 personas) por claves de Catastro mal leídas. El aforo declarado
          en el documento no se toca. */}

      {/* Accesibilidad con semáforo — solo si hay edificación (no aplica a suelo/cultivo) */}
      {hayEdificacion && (
        <div style={cellStyle} data-testid="catastro-accesibilidad" data-atenuado={dudosa ? 'true' : undefined}>
          <span style={labelStyle}>Accesibilidad</span>
          <span style={{ ...valueStyle, ...(dudosa ? { color: colors.fg.subtle } : {}) }}>
            <span
              style={semaphoreStyle(dudosa ? colors.fg.subtle : accesibilidadColor(data.accesibilidad))}
              aria-hidden="true"
            />
            {data.accesibilidadLabel}
          </span>
          {dudosa && <span style={noVerificadaStyle}>según parcela no verificada</span>}
        </div>
      )}

      {/* Cultivos (cat-rich-1) — solo rústicas; bloque ancho condicional.
          DOP-CRUI-2=a: ausente si el array no viene o está vacío. */}
      {cultivos.length > 0 && (
        <div style={cultivosBlockStyle} data-testid="catastro-cultivos">
          <span style={labelStyle}>Cultivos</span>
          <div style={cultivosListStyle}>
            {cultivos.slice(0, MAX_CULTIVOS).map((c, i) => {
              const { denom, sup } = readCultivo(c);
              return (
                <span key={i} style={cultivoItemStyle}>
                  {denom}
                  {sup > 0 && (
                    <span style={cultivoSupStyle}>{sup.toLocaleString('es-ES')} m²</span>
                  )}
                </span>
              );
            })}
            {cultivos.length > MAX_CULTIVOS && (
              <span style={cultivoMoreStyle}>+{cultivos.length - MAX_CULTIVOS} más</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CatastroInlinePanel;
